import { Button, Select, type Column, type SelectOption } from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PaginationControls } from "@/components/PaginationControls";
import {
  FirmChip,
  ProfilesFilterCell,
  ProfilesListFilters,
  RegionNodeChip,
  useCompanyNames,
} from "@/components/Profiles/ListFilters";
import {
  firmProfileKeys,
  firmProfilesService,
  type BusinessType,
  type FirmProfile,
} from "@/api/profiles";
import { parseRegionKind } from "@/hooks/useRegions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesList.module.css";

const PAGE_SIZE = 20;

const BUSINESS_TYPE_OPTIONS: SelectOption[] = [
  { value: "", label: "All business types" },
  { value: "lawfirm", label: "Law firm" },
  { value: "set", label: "Set" },
  { value: "network", label: "Network" },
];

const BUSINESS_TYPE_LABEL: Record<BusinessType, string> = {
  lawfirm: "Law firm",
  set: "Set",
  network: "Network",
};

function stripHtml(input: string | null | undefined): string {
  if (!input) return "";
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(input, "text/html");
    return (doc.body.textContent ?? "").trim();
  }
  // Non-browser fallback: iterate until the result is stable so that
  // partially-matched/nested patterns can't leave `<script` fragments behind.
  let prev = "";
  let out = input;
  while (out !== prev) {
    prev = out;
    out = out.replace(/<[^>]*>?/g, "");
  }
  return out.trim();
}

function displayNameFor(profile: FirmProfile): string {
  const stripped = stripHtml(profile.theFirm ?? null);
  return stripped ? stripped.slice(0, 80) : `Firm ${profile.companyId.slice(0, 8)}`;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

export function FirmProfilesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = searchParams.get("companyId") ?? undefined;
  const status = searchParams.get("status") ?? undefined;
  const q = searchParams.get("q") ?? undefined;
  const regionKind = parseRegionKind(searchParams.get("regionKind"));
  const regionId = searchParams.get("regionId") ?? undefined;
  const businessType = (searchParams.get("businessType") ?? "") as BusinessType | "";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  const [deleteTarget, setDeleteTarget] = useState<FirmProfile | null>(null);

  const filters = useMemo(
    () => ({
      companyId,
      status,
      q,
      regionKind,
      regionId,
      businessType: businessType || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, status, q, regionKind, regionId, businessType, offset],
  );

  const listQuery = useQuery({
    queryKey: firmProfileKeys.list(filters),
    queryFn: () => firmProfilesService.list(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => firmProfilesService.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: firmProfileKeys.all });
      setDeleteTarget(null);
    },
  });

  const rows = listQuery.data?.data ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Batch firm-name resolution for the page so each row's chip doesn't N+1.
  const companyIds = useMemo(() => rows.map(r => r.companyId), [rows]);
  const names = useCompanyNames(companyIds);

  function setParam(key: string, value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      // Reset page when filters change.
      if (key !== "page") next.delete("page");
      return next;
    });
  }

  // Columns for the shared <DataTable>, preserving the exact column order and
  // content of the previous hand-rolled table. Columns that don't map to a
  // single field (Regions, Actions) use a valid `keyof FirmProfile` accessor
  // purely to satisfy the type and supply a `render`. The Regions column opts
  // into `wrap` so the region chips can flow onto multiple lines.
  const columns: Column<FirmProfile>[] = [
    {
      header: "Firm",
      accessor: "companyId",
      render: (_value, row) => <FirmChip companyId={row.companyId} name={names[row.companyId]} />,
    },
    {
      header: "Name",
      accessor: "theFirm",
      render: (_value, row) => {
        const display = displayNameFor(row);
        return (
          <span data-testid={`firm-profile-row-${row.id}`}>
            <Link to={ROUTES.profiles.firmProfileDetail(row.id)} className={styles.titleLink}>
              {display}
            </Link>
          </span>
        );
      },
    },
    {
      header: "Profile type",
      accessor: "profileType",
      render: (_value, row) => BUSINESS_TYPE_LABEL[row.profileType],
    },
    {
      header: "Regions",
      accessor: "regions",
      wrap: true,
      render: (_value, row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(row.regions ?? []).map(r => (
            <RegionNodeChip key={`${r.kind}:${r.id}`} region={r} />
          ))}
        </div>
      ),
    },
    {
      header: "Language",
      accessor: "languageCode",
      render: (_value, row) => row.languageCode ?? "—",
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={styles.statusPill} data-status={row.status}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => (
        <span className={styles.muted}>{formatDateShort(row.updatedAt)}</span>
      ),
    },
    {
      header: "",
      accessor: "id",
      render: (_value, row) => {
        const display = displayNameFor(row);
        return (
          <div className={styles.actions}>
            <Button
              variant="outlined"
              size="small"
              aria-label={`View ${display}`}
              onClick={() => navigate(ROUTES.profiles.firmProfileDetail(row.id))}
            >
              View
            </Button>
            <Button
              variant="outlined"
              size="small"
              aria-label={`Edit ${display}`}
              onClick={() => navigate(ROUTES.profiles.firmProfileDetail(row.id))}
            >
              Edit
            </Button>
            <Button
              variant="text"
              color="error"
              size="small"
              aria-label={`Delete ${display}`}
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Firm profiles">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.profiles.firmProfileNew)}
        >
          New firm profile
        </Button>
      </PageHeader>

      <ProfilesListFilters enabledFilters={{ company: true, status: true, q: true, region: true }}>
        <ProfilesFilterCell>
          <Select
            label="Business type"
            value={businessType}
            options={BUSINESS_TYPE_OPTIONS}
            onChange={e => setParam("businessType", e.target.value || null)}
            size="small"
          />
        </ProfilesFilterCell>
      </ProfilesListFilters>

      <div className={styles.contentSection}>
        {listQuery.isPending ? (
          <p role="status" className={styles.muted}>
            Loading firm profiles…
          </p>
        ) : listQuery.isError ? (
          <p role="alert" className={styles.errorBanner}>
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Failed to load firm profiles"}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.emptyState}>No firm profiles match your filters.</p>
        ) : (
          <>
            <DataTable data={rows} columns={columns} rowKey="id" />

            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + rows.length}
              totalCount={total}
              itemName="firm profiles"
              onPageChange={p => setParam("page", String(p))}
            />
          </>
        )}
      </div>

      {deleteTarget && (
        <ConfirmationModal
          title="Delete firm profile"
          message={`Are you sure you want to delete '${displayNameFor(deleteTarget)}'?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(deleteTarget.id);
          }}
        />
      )}
    </div>
  );
}

export default FirmProfilesList;
